<template>
  <div v-if="visible" class="compare-overlay" @click.self="$emit('close')">
    <div class="compare-dialog">
      <div class="compare-header">
        <div>
          <h5 class="compare-title">{{ $t('training.judge.compare_title') || 'Comparar rondas' }}</h5>
          <p class="compare-subtitle">
            {{ $t('training.judge.compare_subtitle') || 'Compara tus rondas ya resueltas con la misma evaluación hecha por otros jueces o con la original publicada.' }}
          </p>
        </div>
        <button type="button" class="compare-close" @click="$emit('close')">×</button>
      </div>

      <p v-if="optionsLoading" class="compare-loading-inline">
        {{ $t('training.loading') || 'Cargando...' }}
      </p>

      <div v-else class="compare-toolbar">
        <div class="compare-select-block">
          <label class="compare-label">{{ $t('training.judge.compare_left') || 'Mis rondas' }}</label>
          <select
            :value="leftRoundId || ''"
            class="compare-select"
            :disabled="!leftOptions.length"
            @change="$emit('select-left', $event.target.value)"
          >
            <option value="">{{ leftPlaceholder }}</option>
            <option v-for="option in leftOptions" :key="`left-${option.id}`" :value="option.id">
              {{ option.label }}
            </option>
          </select>
          <p v-if="!leftOptions.length" class="compare-hint compare-hint--disabled">
            {{ $t('training.judge.compare_no_rounds') || 'No tienes rondas finalizadas para comparar.' }}
          </p>
        </div>

        <div class="compare-select-block">
          <div class="compare-toggle-row">
            <label class="compare-label">{{ rightLabel }}</label>
            <div class="form-check form-switch compare-mode-switch">
              <input
                id="judge-compare-original-switch"
                class="form-check-input"
                type="checkbox"
                :checked="compareWithOriginal"
                :disabled="originalSwitchDisabled"
                @change="$emit('toggle-original', $event.target.checked)"
              >
              <label class="compare-switch-label" for="judge-compare-original-switch">
                {{ $t('training.judge.compare_original_toggle') || 'Original publicada' }}
              </label>
            </div>
          </div>

          <select
            :value="rightRoundId || ''"
            class="compare-select"
            :disabled="rightSelectDisabled"
            @change="$emit('select-right', $event.target.value)"
          >
            <option value="">{{ rightPlaceholder }}</option>
            <option v-for="option in visibleRightOptions" :key="`right-${option.id}`" :value="option.id">
              {{ option.label }}
            </option>
          </select>

          <p v-if="rightHelpText" class="compare-hint" :class="{ 'compare-hint--disabled': rightSelectDisabled }">
            {{ rightHelpText }}
          </p>
        </div>

        <button type="button" class="compare-load-btn" :disabled="!canCompare || loading" @click="$emit('compare')">
          {{ loading ? ($t('training.loading') || 'Cargando...') : ($t('training.supervisor.compare_action') || 'Comparar') }}
        </button>
      </div>

      <p v-if="error" class="compare-error">{{ error }}</p>

      <div
        v-if="(commonInfoItems.length || hasAnyVideo) && !loading"
        class="compare-common-grid"
        :class="{ 'compare-common-grid--with-media': hasAnyVideo }"
      >
        <div v-for="item in commonInfoItems" :key="item.key" class="compare-common-card">
          <span class="compare-common-label">{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
        </div>

        <button
          v-if="hasAnyVideo"
          type="button"
          class="compare-common-card compare-common-card--video"
          :title="mediaCollapsed
            ? ($t('training.supervisor.compare_expand_video') || 'Mostrar video')
            : ($t('training.supervisor.compare_collapse_video') || 'Ocultar video')"
          :aria-label="mediaCollapsed
            ? ($t('training.supervisor.compare_expand_video') || 'Mostrar video')
            : ($t('training.supervisor.compare_collapse_video') || 'Ocultar video')"
          @click="toggleMediaCollapsed"
        >
          <i class="fa-solid fa-video tech-icon text-primary" aria-hidden="true"></i>
        </button>
      </div>

      <div v-if="sharedVideoUrl && !loading && !mediaCollapsed" class="compare-shared-media">
        <YoutubePlayer
          v-if="isYoutubeUrl(sharedVideoUrl)"
          :video-url="sharedVideoUrl"
          :can-delete="false"
          :read-only="true"
        />
        <LocalVideoPlayer
          v-else
          :video-url="sharedVideoUrl"
          :read-only="true"
        />
      </div>

      <div class="compare-content" :class="{ 'compare-content--loading': loading }">
        <div v-if="loading" class="compare-loading">
          {{ $t('training.loading') || 'Cargando rondas...' }}
        </div>

        <template v-else>
          <div class="compare-column">
            <CompareRoundCard
              side="left"
              :payload="payload && payload.left ? payload.left : null"
              :hide-media="Boolean(sharedVideoUrl) || mediaCollapsed"
            />
          </div>

          <div class="compare-column">
            <CompareRoundCard
              side="right"
              :payload="payload && payload.right ? payload.right : null"
              :hide-media="Boolean(sharedVideoUrl) || mediaCollapsed"
            />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import CompareRoundCard from '@/components/training/CompareRoundCard.vue';
import YoutubePlayer from '@/services/YoutubePlayer.vue';
import LocalVideoPlayer from '@/services/LocalVideoPlayer.vue';

export default {
  name: 'JudgeTrainingComparePanel',
  components: {
    CompareRoundCard,
    YoutubePlayer,
    LocalVideoPlayer,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    leftOptions: {
      type: Array,
      default: () => [],
    },
    leftRoundId: {
      type: [String, Number, null],
      default: null,
    },
    rightOptions: {
      type: Array,
      default: () => [],
    },
    rightRoundId: {
      type: [String, Number, null],
      default: null,
    },
    compareWithOriginal: {
      type: Boolean,
      default: false,
    },
    originalOption: {
      type: Object,
      default: null,
    },
    originalDisabledReason: {
      type: String,
      default: '',
    },
    otherJudgeDisabledReason: {
      type: String,
      default: '',
    },
    optionsLoading: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    payload: {
      type: Object,
      default: null,
    },
    error: {
      type: String,
      default: '',
    },
  },
  emits: ['close', 'select-left', 'select-right', 'toggle-original', 'compare'],
  data() {
    return {
      mediaCollapsed: true,
    };
  },
  computed: {
    leftPlaceholder() {
      return this.$t('training.judge.compare_left_placeholder') || 'Selecciona una ronda resuelta';
    },
    rightLabel() {
      return this.compareWithOriginal
        ? (this.$t('training.judge.compare_right_original') || 'Original publicada')
        : (this.$t('training.judge.compare_right_judges') || 'Otros jueces');
    },
    originalSwitchDisabled() {
      return !this.leftRoundId || (!this.originalOption && !this.originalDisabledReason);
    },
    visibleRightOptions() {
      if (this.compareWithOriginal) {
        return this.originalOption ? [this.originalOption] : [];
      }

      return this.rightOptions;
    },
    rightSelectDisabled() {
      if (!this.leftRoundId) return true;
      if (this.compareWithOriginal) return !this.originalOption;
      return !this.rightOptions.length;
    },
    rightPlaceholder() {
      if (!this.leftRoundId) {
        return this.$t('training.judge.compare_select_left_first') || 'Selecciona primero una ronda a la izquierda';
      }

      if (this.compareWithOriginal) {
        return this.originalOption
          ? (this.$t('training.judge.compare_right_original_placeholder') || 'Original publicada disponible')
          : (this.$t('training.judge.compare_original_unavailable') || 'Original no disponible');
      }

      return this.$t('training.judge.compare_right_placeholder') || 'Selecciona otra evaluación';
    },
    rightHelpText() {
      if (!this.leftRoundId) return '';
      return this.compareWithOriginal ? this.originalDisabledReason : this.otherJudgeDisabledReason;
    },
    canCompare() {
      return Boolean(this.leftRoundId && this.rightRoundId && !this.optionsLoading);
    },
    leftVideoUrl() {
      return this.extractVideoUrl(this.payload?.left);
    },
    rightVideoUrl() {
      return this.extractVideoUrl(this.payload?.right);
    },
    sharedVideoUrl() {
      if (!this.leftVideoUrl || !this.rightVideoUrl) return '';
      return this.normalizeVideoUrl(this.leftVideoUrl) === this.normalizeVideoUrl(this.rightVideoUrl)
        ? this.leftVideoUrl
        : '';
    },
    leftRound() {
      return this.payload?.left?.round || null;
    },
    rightRound() {
      return this.payload?.right?.round || null;
    },
    hasAnyVideo() {
      return Boolean(this.leftVideoUrl || this.rightVideoUrl);
    },
    commonInfoItems() {
      const items = [];

      const roundName = this.leftRound?.round_name || this.rightRound?.round_name || '';
      if (roundName) {
        items.push({
          key: 'round_name',
          label: this.$t('training.round_name') || 'Nombre de la ronda',
          value: roundName,
        });
      }

      const athlete = this.formatPerson(this.leftRound?.user || this.rightRound?.user || this.leftRound?.athlete || this.rightRound?.athlete || null);
      if (athlete && athlete !== '—') {
        items.push({
          key: 'athlete',
          label: this.$t('training.supervisor.compare_athlete') || 'Atleta',
          value: athlete,
        });
      }

      const status = this.leftRound?.status?.name || this.rightRound?.status?.name || '—';
      if (status && status !== '—') {
        items.push({
          key: 'status',
          label: this.$t('training.view.status') || 'Estado',
          value: status,
        });
      }

      return items;
    },
  },
  watch: {
    visible() {
      this.mediaCollapsed = true;
    },
    payload() {
      this.mediaCollapsed = true;
    },
  },
  methods: {
    extractVideoUrl(payload) {
      return payload?.round?.video_link || payload?.round?.videoUrl || '';
    },
    normalizeVideoUrl(url) {
      const raw = String(url || '').trim();
      if (!raw) return '';

      try {
        const parsed = new URL(raw);
        parsed.hash = '';
        if ((parsed.protocol === 'http:' && parsed.port === '80') || (parsed.protocol === 'https:' && parsed.port === '443')) {
          parsed.port = '';
        }
        if (parsed.pathname.length > 1) {
          parsed.pathname = parsed.pathname.replace(/\/+$/, '');
        }
        parsed.searchParams.sort();
        return parsed.toString();
      } catch (_) {
        return raw.replace(/\/+$/, '');
      }
    },
    isYoutubeUrl(url) {
      return /(?:youtube\.com|youtu\.be)/i.test(String(url || ''));
    },
    formatPerson(person) {
      if (!person) return '—';
      const label = [person.name, person.surname].filter(Boolean).join(' ').trim();
      return label || person.email || (person.id ? `#${person.id}` : '—');
    },
    toggleMediaCollapsed() {
      this.mediaCollapsed = !this.mediaCollapsed;
    },
  },
};
</script>

<style scoped>
.compare-overlay {
  position: fixed;
  inset: 0;
  z-index: 1300;
  background: rgba(15, 23, 42, 0.58);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.compare-dialog {
  width: min(1400px, 100%);
  max-height: calc(100vh - 32px);
  overflow: auto;
  background: #f8fafc;
  border-radius: 18px;
  padding: 18px;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.28);
}

.compare-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.compare-title {
  margin: 0;
  color: #0f172a;
}

.compare-subtitle {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 13px;
}

.compare-close {
  border: none;
  background: transparent;
  color: #475569;
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
}

.compare-loading-inline {
  margin-top: 14px;
  color: #475569;
}

.compare-toolbar {
  margin-top: 14px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
  gap: 10px;
  align-items: end;
}

.compare-select-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.compare-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}

.compare-mode-switch {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.compare-switch-label {
  color: #334155;
  font-size: 13px;
  font-weight: 600;
}

.compare-label {
  color: #475569;
  font-size: 13px;
  font-weight: 600;
}

.compare-select {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px 12px;
  background: #fff;
  color: #0f172a;
}

.compare-hint {
  margin: 0;
  font-size: 12px;
  color: #64748b;
}

.compare-hint--disabled {
  color: #9a3412;
}

.compare-load-btn {
  border: none;
  border-radius: 10px;
  padding: 10px 14px;
  cursor: pointer;
  font-weight: 700;
  background: #1d4ed8;
  color: #fff;
}

.compare-load-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.compare-error {
  margin: 12px 0 0;
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  padding: 10px 12px;
}

.compare-common-grid {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.compare-common-grid--with-media {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.compare-common-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 10px 12px;
}

.compare-common-card--video {
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  cursor: pointer;
}

.compare-common-label {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}

.compare-shared-media {
  margin-top: 14px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 14px;
}

.compare-content {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.compare-content--loading {
  min-height: 240px;
}

.compare-loading {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 240px;
  color: #475569;
}

.compare-column {
  min-width: 0;
}

@media (max-width: 1100px) {
  .compare-dialog {
    padding: 14px;
    border-radius: 16px;
  }

  .compare-toolbar {
    gap: 8px;
  }

  .compare-content {
    gap: 10px;
  }

  .compare-shared-media {
    padding: 10px;
  }
}

@media (max-width: 820px) {
  .compare-overlay {
    padding: 10px;
  }

  .compare-dialog {
    padding: 12px;
    border-radius: 14px;
  }

  .compare-title {
    font-size: 17px;
  }

  .compare-subtitle,
  .compare-label,
  .compare-switch-label,
  .compare-hint {
    font-size: 12px;
  }

  .compare-select,
  .compare-load-btn {
    padding: 8px 10px;
    font-size: 13px;
  }

  .compare-content {
    gap: 8px;
  }

  .compare-common-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .compare-common-grid--with-media {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 680px) {
  .compare-dialog {
    padding: 10px;
  }

  .compare-toolbar {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  .compare-load-btn {
    grid-column: 1 / -1;
    width: 100%;
  }

  .compare-content {
    gap: 6px;
  }

  .compare-common-grid {
    gap: 6px;
  }

  .compare-toggle-row {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }
}

@media (max-width: 640px) {
  .compare-dialog {
    padding: 10px;
  }

  .compare-common-card {
    border-radius: 10px;
    padding: 8px;
  }

  .compare-common-label {
    font-size: 11px;
  }
}
</style>